import type { ComponentType } from 'react';
import { useEffect } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { StartAPetitionPageData } from '#src/app/pages/startAPetitionV2/pageContext';
import { useStartAPetitionPageData } from '#src/app/pages/startAPetitionV2/pageContext';
import { treatExperiment } from '#src/app/shared/api/experiments';

export type StepExperimentProps = {
	/**
	 * Key of the experiment in the page-data experiments payload. The experiment must be an
	 * internal-experiment-service experiment queried with `treat: false` — treatment is recorded
	 * here, on first render of a touched step, so bucketed users who never reach the step are
	 * not counted as exposed.
	 */
	experiment: keyof StartAPetitionPageData['experiments'];
	/**
	 * The untouched control step component. Also rendered for `fake_control` and any variation
	 * without an entry in `variants`.
	 */
	control: ComponentType;
	/**
	 * Experiment-owned copies of the step component, keyed by variation name
	 * (e.g. `{ variant_a: CreateVariantA }`).
	 */
	variants: Readonly<Partial<Record<string, ComponentType>>>;
};

type Result = ModelHookResult<{ Component: ComponentType }>;

/**
 * Model hook for `StepExperiment`: treats the experiment on mount for every arm (`control` and
 * `fake_control` included) — except when the resolution is a `fallback` (config disabled /
 * resolution fell back to the default rather than a real assignment) — and resolves which step
 * component the assigned variation renders.
 */
export function useStepExperiment({ experiment, control, variants }: StepExperimentProps): Result {
	const utilityContext = useUtilityContext();
	const { data } = useStartAPetitionPageData();
	const resolved = data.experiments[experiment];

	useEffect(() => {
		const { type, experimentName, fallback } = resolved;
		if (type !== 'experiment-service' || fallback) return;
		void treatExperiment(utilityContext, experimentName);
	}, [resolved, utilityContext]);

	return { data: { Component: (!resolved.fallback && variants[resolved.variation]) || control } };
}
